import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { EventTicket } from "@prisma/client";

import { IoTimeOutline } from "react-icons/io5";
import { FiPlus, FiMinus } from "react-icons/fi";

import { cn } from "~/utils/fontsConfig";
import { toastHandler } from "~/components/ToastHandler";
import { Skeleton } from "~/components/v4/shadcn/Skeleton";
import { SystemMessage } from "~/components/v4/themed/SystemMessage";

import { refetchAction } from "~/app/beta/events/[eventSlug]/_components/rsvp_components/actions";

import {
    canProcessPayments,
    createCheckoutSession,
    getEventTickets,
} from "../actions/action";
import { RSVPStepsFormInputData } from "../validators";

export const TicketPurchaseOptions = ({
    onNext,
    eventSlug,
    data,
}: {
    onNext: () => void;
    eventSlug: string;
    data: RSVPStepsFormInputData;
}) => {
    const [quantities, setQuantities] = useState<Map<string, number>>(
        new Map(),
    );

    const { data: isConfiguredToProcessPayments, isLoading } = useQuery({
        queryKey: ["canProcessPayments", eventSlug],
        queryFn: () => canProcessPayments(eventSlug),
        cacheTime: 0,
        staleTime: 0,
    });

    const { data: tickets, isLoading: isLoadingTickets } = useQuery({
        queryKey: ["tickets", eventSlug],
        queryFn: () => getEventTickets(eventSlug),
        enabled: isConfiguredToProcessPayments,
    });

    const handleQuantityChange = (ticketId: string, newQuantity: number) => {
        setQuantities((prev) => {
            const ticket = tickets?.find((t) => t.id === ticketId);

            const maxQuantity = ticket?.capacity;
            const soldCount = ticket?.soldCount ?? 0;

            const quantity = Math.max(
                0,
                Math.min(
                    newQuantity,
                    maxQuantity ? maxQuantity - soldCount : Infinity,
                ),
            );

            if (quantity === 0) {
                prev.delete(ticketId);
            } else {
                prev.set(ticketId, quantity);
            }

            return new Map(prev);
        });
    };

    const handleTicketPurchase = async () => {
        // if checkout is not configured, don't proceed, will never be false when this code is executed.
        if (!isConfiguredToProcessPayments) return;

        const selectedTickets =
            tickets
                ?.filter((ticket) => (quantities.get(ticket.id) ?? 0) > 0)
                .map((ticket) => ({
                    ticketId: ticket.id,
                    quantity: quantities.get(ticket.id) as number,
                })) ?? [];

        if (selectedTickets.length === 0) return;

        toastHandler({
            loadingMessage: "Creating checkout session...",
            successMessage: "Checkout session created successfully",
            errorMessage: "Failed to create checkout session",
            operation: async () => {
                const checkoutUrl = await createCheckoutSession({
                    tickets: selectedTickets,
                    userInfo: data.register,
                });

                window.location.href = checkoutUrl;
            },
            onSuccess: () => {
                refetchAction(eventSlug);
            },
            onError: (error) => {
                console.error("Failed to create checkout session", error);
            },
        });
    };

    const totalQuantity = Array.from(quantities.values()).reduce(
        (acc, curr) => acc + curr,
        0,
    );

    if (isLoading || isLoadingTickets) {
        return (
            <div className="flex flex-col gap-4">
                <Skeleton className="h-40 w-full" />
            </div>
        );
    }

    if (!isConfiguredToProcessPayments) {
        return (
            <div className="flex flex-col gap-4">
                <SystemMessage
                    title="Checkout is Disabled"
                    message="This event is currently not configured to accept payments. or disabled by the River Admin. If you believe this to be an error, please contact the host."
                    showIcon={false}
                />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
                <div className="text-sm text-white">
                    Select the tickets you want to purchase.
                </div>
            </div>

            <div className="flex flex-col gap-6">
                {tickets?.map((ticket) => (
                    <TicketCard
                        key={ticket.id}
                        ticket={ticket}
                        quantity={quantities.get(ticket.id) ?? 0}
                        setQuantity={(quantity) =>
                            handleQuantityChange(ticket.id, quantity)
                        }
                    />
                ))}
            </div>

            <button
                onClick={handleTicketPurchase}
                disabled={quantities.size === 0}
                className={cn(
                    "mt-4 rounded-lg bg-v3-clover px-4 py-2 text-white",
                    quantities.size === 0 && "cursor-not-allowed opacity-50",
                )}
            >
                Purchase Tickets ({totalQuantity})
            </button>
        </div>
    );
};

export const TicketCard = ({
    ticket,
    quantity,
    setQuantity,
}: {
    ticket: EventTicket;
    quantity: number;
    setQuantity: (quantity: number) => void;
}) => {
    const getTicketStatus = () => {
        if (ticket.capacity === null) return;
        if (ticket.soldCount >= (ticket.capacity ?? 0)) return "Sold out";
        if (ticket.requiresApproval) return "Requires approval";

        return;
    };

    const status = getTicketStatus();

    const ticketsLeft = ticket.capacity
        ? ticket.capacity - (ticket.soldCount ?? 0) - quantity
        : null;

    const isPaid = ticket.price !== null && ticket.price > 0;

    const handleQuantityClick = (e: React.MouseEvent, newQuantity: number) => {
        e.stopPropagation();
        setQuantity(newQuantity);
    };

    return (
        <div className="flex flex-col gap-4 rounded-[10px] bg-v3-N-500 p-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="font-medium">{ticket.name}</span>
                    <span
                        className={cn(
                            "rounded-md p-[2px] px-2 text-sm",
                            status === "Sold out"
                                ? "bg-red-500/10 text-red-500"
                                : status === "Requires approval"
                                  ? "bg-yellow-500/10 text-yellow-500"
                                  : "",
                        )}
                    >
                        {status}
                    </span>
                </div>

                <div className="grid min-w-fit grid-cols-3 items-center gap-2">
                    <button
                        onClick={(e) => handleQuantityClick(e, quantity - 1)}
                        disabled={quantity <= 0}
                        className="cursor-pointer rounded-full bg-white p-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <FiMinus className="h-4 w-4 text-black" />
                    </button>

                    <span className="text-center text-lg text-white">
                        {quantity}
                    </span>

                    <button
                        onClick={(e) => handleQuantityClick(e, quantity + 1)}
                        disabled={
                            ticket.capacity !== null &&
                            ticket.soldCount + quantity >= ticket.capacity
                        }
                        className="cursor-pointer rounded-full bg-white p-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <FiPlus className="h-4 w-4 text-black" />
                    </button>
                </div>
            </div>

            <div className="flex flex-col gap-4">
                <p className="flex gap-2 text-3xl font-semibold">
                    <span className="text-v3-N-300">$</span>
                    <span className="text-3xl font-semibold">
                        {isPaid ? ticket.price : "Free"}
                    </span>
                </p>
            </div>

            {!!ticketsLeft && ticketsLeft <= 5 && (
                <div className="flex items-center gap-2 border-t border-v3-N-300/20 pt-4">
                    <div className="flex items-center justify-center rounded-md bg-v3-N-300/20 p-1">
                        <IoTimeOutline className="h-4 w-4 text-white" />
                    </div>

                    <p className="flex items-center gap-1 text-sm text-v3-N-300">
                        <span>{ticketsLeft}</span>
                        <span>{ticketsLeft === 1 ? "spot" : "spots"} left</span>
                    </p>
                </div>
            )}
        </div>
    );
};
